<template>
  <teleport to="body">
    <div class="modal-overlay" @click="$emit('close')">
      <div class="modal-content" @click.stop>
        <button class="modal-close" @click="$emit('close')">×</button>

      <h2>{{ isCreateAccount ? 'Create Account' : 'Sign In' }}</h2>

      <div v-if="!showEmailForm" class="provider-buttons">
      
        <button @click="showEmailForm = true" class="provider-button email-button">
          <svg class="provider-icon" viewBox="0 0 24 24">
            <path fill="currentColor" d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
          </svg>
          Continue with Email
        </button>
      </div>

      <div v-else class="email-form">
        <button @click="showEmailForm = false" class="back-button">
          ← Back to sign in options
        </button>

        <form @submit.prevent="handleEmailSubmit">
          <div v-if="isCreateAccount" class="form-group">
            <label for="displayName">Name</label>
            <input
              id="displayName"
              v-model="displayName"
              type="text"
              placeholder="Your name"
              :disabled="isLoading"
            />
          </div>

          <div class="form-group">
            <label for="email">Email</label>
            <input
              id="email"
              v-model="email"
              type="email"
              placeholder="you@example.com"
              required
              :disabled="isLoading"
            />
          </div>

          <div v-if="!showResetPassword" class="form-group">
            <label for="password">Password</label>
            <input
              id="password"
              v-model="password"
              type="password"
              placeholder="••••••••"
              required
              :disabled="isLoading"
            />
          </div>

          <p v-if="error" class="error-message">{{ error }}</p>
          <p v-if="successMessage" class="success-message">{{ successMessage }}</p>

          <button type="submit" :disabled="isLoading" class="submit-button">
            <span v-if="isLoading" class="loading-spinner"></span>
            <span v-else>{{ submitButtonText }}</span>
          </button>
        </form>

        <div class="form-footer">
          <button
            v-if="!isCreateAccount && !showResetPassword"
            @click="showResetPassword = true"
            class="link-button"
          >
            Forgot password?
          </button>

          <button
            v-if="showResetPassword"
            @click="showResetPassword = false"
            class="link-button"
          >
            Back to sign in
          </button>

          <button
            v-if="!showResetPassword"
            @click="isCreateAccount = !isCreateAccount"
            class="link-button"
          >
            {{ isCreateAccount ? 'Already have an account? Sign in' : "Don't have an account? Create one" }}
          </button>
        </div>
      </div>
      </div>
    </div>
  </teleport>
</template>

<script>
import { ref, computed } from 'vue'

export default {
  name: 'SignInModal',
  props: {
    authError: {
      type: String,
      default: null
    }
  },
  emits: ['close', 'google-sign-in', 'apple-sign-in', 'email-sign-in', 'create-account', 'password-reset'],
  setup(props, { emit }) {
    const showEmailForm = ref(false)
    const isCreateAccount = ref(false)
    const showResetPassword = ref(false)
    const isLoading = ref(false)
    const email = ref('')
    const password = ref('')
    const displayName = ref('')
    const error = ref(null)
    const successMessage = ref(null)

    const submitButtonText = computed(() => {
      if (showResetPassword.value) return 'Send Reset Email'
      if (isCreateAccount.value) return 'Create Account'
      return 'Sign In'
    })

    const handleGoogleSignIn = async () => {
      isLoading.value = true
      error.value = null
      emit('google-sign-in')
    }

    const handleAppleSignIn = async () => {
      isLoading.value = true
      error.value = null
      emit('apple-sign-in')
    }

    const handleEmailSubmit = async () => {
      isLoading.value = true
      error.value = null
      successMessage.value = null

      if (showResetPassword.value) {
        emit('password-reset', email.value)
      } else if (isCreateAccount.value) {
        emit('create-account', {
          email: email.value,
          password: password.value,
          displayName: displayName.value
        })
      } else {
        emit('email-sign-in', {
          email: email.value,
          password: password.value
        })
      }
    }

    const setLoading = (loading) => {
      isLoading.value = loading
    }

    const setError = (err) => {
      error.value = err
      isLoading.value = false
    }

    const setSuccess = (msg) => {
      successMessage.value = msg
      isLoading.value = false
    }

    return {
      showEmailForm,
      isCreateAccount,
      showResetPassword,
      isLoading,
      email,
      password,
      displayName,
      error,
      successMessage,
      submitButtonText,
      handleGoogleSignIn,
      handleAppleSignIn,
      handleEmailSubmit,
      setLoading,
      setError,
      setSuccess
    }
  }
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10000;
  backdrop-filter: blur(3px);
}

.modal-content {
  background: rgba(26, 26, 26, 0.95);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border-radius: 12px;
  padding: 2rem;
  max-width: 400px;
  width: 90%;
  position: relative;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.modal-close {
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.5);
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0.5rem;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.2s;
}

.modal-close:hover {
  background: rgba(255, 255, 255, 0.1);
  color: white;
}

h2 {
  color: #ffa500;
  margin: 0 0 1.5rem;
  font-size: 1.5rem;
  font-weight: 500;
  text-align: center;
}

.provider-buttons {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.provider-button {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  width: 100%;
  padding: 0.875rem 1rem;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.provider-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.provider-icon {
  width: 20px;
  height: 20px;
}

.google-button {
  background: white;
  color: #333;
}

.google-button:hover:not(:disabled) {
  background: #f5f5f5;
}

.apple-button {
  background: black;
  color: white;
}

.apple-button:hover:not(:disabled) {
  background: #1a1a1a;
}

.email-button {
  background: rgba(255, 255, 255, 0.1);
  color: white;
}

.email-button:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.15);
}

.back-button {
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.875rem;
  cursor: pointer;
  padding: 0;
  margin-bottom: 1.5rem;
  transition: color 0.2s;
}

.back-button:hover {
  color: white;
}

.form-group {
  margin-bottom: 1rem;
}

.form-group label {
  display: block;
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.875rem;
  margin-bottom: 0.5rem;
}

.form-group input {
  width: 100%;
  padding: 0.75rem 1rem;
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  color: white;
  font-size: 0.9rem;
}

.form-group input:focus {
  outline: none;
  border-color: #ffa500;
}

.form-group input::placeholder {
  color: rgba(255, 255, 255, 0.4);
}

.error-message {
  color: #ff6b6b;
  font-size: 0.875rem;
  margin: 0.75rem 0;
  padding: 0.75rem;
  background: rgba(255, 107, 107, 0.1);
  border-radius: 6px;
}

.success-message {
  color: #4ade80;
  font-size: 0.875rem;
  margin: 0.75rem 0;
  padding: 0.75rem;
  background: rgba(74, 222, 128, 0.1);
  border-radius: 6px;
}

.submit-button {
  width: 100%;
  padding: 0.875rem;
  background: #ffa500;
  border: none;
  border-radius: 8px;
  color: black;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;
  margin-top: 0.5rem;
}

.submit-button:hover:not(:disabled) {
  background: #ff9500;
}

.submit-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.loading-spinner {
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid rgba(0, 0, 0, 0.3);
  border-radius: 50%;
  border-top-color: black;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.form-footer {
  margin-top: 1.5rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
}

.link-button {
  background: none;
  border: none;
  color: #ffa500;
  font-size: 0.875rem;
  cursor: pointer;
  padding: 0.25rem;
  transition: color 0.2s;
}

.link-button:hover {
  color: #ff9500;
  text-decoration: underline;
}
</style>
